'use client';

import React, { useState } from 'react';
import { X, ScanLine, AlertCircle, Plus, Check } from 'lucide-react';
import { ProductItem } from './ProductsCategoryList';

interface StockEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  products: ProductItem[];
  onSubmitStockEntry: (payload: {
    productId: string;
    quantity: number;
    costPrice?: number;
    notes?: string;
  }) => Promise<void>;
}

export default function StockEntryModal({
  isOpen,
  onClose,
  products,
  onSubmitStockEntry
}: StockEntryModalProps) {
  const [selectedProductId, setSelectedProductId] = useState(products[0]?.id || '');
  const [quantity, setQuantity] = useState('10');
  const [costPrice, setCostPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  if (!isOpen) return null;

  const currentProduct = products.find(p => p.id === selectedProductId) || products[0];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProductId) {
      setErrorMsg('Seleccione un producto.');
      return;
    }
    if (!quantity || Number(quantity) <= 0) {
      setErrorMsg('Ingrese una cantidad válida mayor a cero.');
      return;
    }

    setSubmitting(true);
    setErrorMsg('');
    try {
      await onSubmitStockEntry({
        productId: selectedProductId,
        quantity: Number(quantity),
        costPrice: costPrice ? Number(costPrice) : undefined,
        notes: notes.trim()
      });
      onClose();
      // Reset
      setQuantity('10');
      setCostPrice('');
      setNotes('');
    } catch (err: any) {
      setErrorMsg(err.message || 'Error registrando ingreso de stock.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl border border-slate-100 animate-in fade-in zoom-in-95 duration-150">
        
        {/* Cabecera */}
        <div className="p-6 pb-4 border-b border-slate-100 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-700">
              <ScanLine className="w-5 h-5 stroke-[2]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 tracking-tight">
                Ingreso de stock
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Recepción física de mercadería y compras
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Selección de Producto */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              Producto a Ingresar
            </label>
            <select
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-900 focus:outline-none focus:border-emerald-500 cursor-pointer"
            >
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} (Stock actual: {p.stock} un.)
                </option>
              ))}
            </select>
            {currentProduct && (
              <div className="text-[11px] text-slate-500 mt-1 flex justify-between">
                <span>Marca: {currentProduct.brand || 'Sin marca'}</span>
                <span>Stock actual: <strong className="text-emerald-700">{currentProduct.stock} un.</strong></span>
              </div>
            )}
          </div>

          {/* Cantidad a Sumar */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              Cantidad a Sumar (Unidades)
            </label>
            <input
              type="number"
              required
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="10"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-bold text-slate-900 focus:outline-none focus:border-emerald-500"
            />
          </div>

          {/* Costo Unitario de Compra (Opcional) */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              Costo Unitario de Compra ($ ARS - Opcional)
            </label>
            <input
              type="number"
              min={0}
              value={costPrice}
              onChange={(e) => setCostPrice(e.target.value)}
              placeholder={currentProduct ? `${currentProduct.costPrice}` : '0'}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-900 focus:outline-none focus:border-emerald-500"
            />
          </div>

          {/* Observaciones o Factura */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              Nº de Remito o Factura / Proveedor
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ej: Remito Proveedor Distribuidora Norte #4092"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-900 focus:outline-none focus:border-emerald-500"
            />
          </div>

          {errorMsg && (
            <div className="p-3 rounded-xl bg-rose-50 text-rose-600 text-xs font-semibold flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Botones */}
          <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl shadow-md transition disabled:opacity-50"
            >
              {submitting ? 'Registrando...' : 'Confirmar Ingreso'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
